import type {FabricObjectPositionOrDimensionKeys} from '@Components/poster-editor/components/custom-item-controls/custom-item-controls.types';
import {degreesToRadians} from '@Utils/math.util';
import type {ItemActionBarAnchor} from './custom-item-action-bar.types';
import {ITEM_ACTION_BAR_MARGIN, ROTATION_HANDLE_REACH} from './custom-item-action-bar.types';

/**
 * Where the action bar sits: centred under the selection's on-screen bounding box. The bar is never rotated,
 * so it hangs below the lowest corner of a rotated item rather than along the item's own bottom edge. When the
 * item is turned far enough that its rotation handle points down, the bar drops below the handle too.
 */
export const getItemActionBarAnchor = (positionProperties: FabricObjectPositionOrDimensionKeys): ItemActionBarAnchor => {
  const {posterScale} = positionProperties;
  const width = positionProperties.scaledWidth * posterScale;
  const height = positionProperties.scaledHeight * posterScale;
  // Fabric reports left/top as the rotated item's top-left corner.
  const left = positionProperties.left * posterScale;
  const top = positionProperties.top * posterScale;

  const angleRadians = degreesToRadians(positionProperties.angle);
  const cos = Math.cos(angleRadians);
  const sin = Math.sin(angleRadians);

  const cornersX = [left, left + width * cos, left - height * sin, left + width * cos - height * sin];
  const cornersY = [top, top + width * sin, top + height * cos, top + width * sin + height * cos];

  const topEdgeMidpointY = top + (width / 2) * sin;
  const rotationHandleY = topEdgeMidpointY - cos * ROTATION_HANDLE_REACH;

  return {
    centerX: (Math.min(...cornersX) + Math.max(...cornersX)) / 2,
    top: Math.max(...cornersY, rotationHandleY) + ITEM_ACTION_BAR_MARGIN,
  };
};
